// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc */
/* eslint-disable ta-code-conventions/has-matching-test */
/* eslint-disable ta-code-conventions/require-jsdoc */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  memo,
  type Node,
} from 'react';
import { useQuery } from 'urql';
import { Typeahead, TypeaheadJsonFetcher } from '@ta/common.typeahead';
import { NavigableList } from '@ta/list.navigable-list';
import { Icon } from '@ta/styleguide.icon';
import { useLocalize } from '@ta/platform.i18n';
import GeoPickerResult from './GeoPickerResult';
import PopularDestinationsQuery from './PopularDestinationsQuery.gql';
import styles from './GeoPicker.css';

type Props = {|
  placeholder?: string,

  /**
   * The geo name, if one has already been selected
   */
  geoName: ?string,

  /**
   * If true, close the geo list when one is selected. This is true in cases where selecting a
   * geo doesn't automatically advance the screen.
   */
  closeOnSelection?: boolean,

  /**
   * If true, include some suggestions based on the current locale/country
   */
  showPopularDestinations?: boolean,

  /**
   * If true, show a "nearby" option
   */
  showNearby?: boolean,

  maxResults?: number,

  /**
   * Called when a geo is selected with the ID and name from the typeahead endpoint
   */
  onChange: (geoId: number, geoName: string) => mixed,

  /**
   * Called when the geo picker is shown or hidden
   */
  onShowHide: (isShown: boolean) => mixed,

  /**
   * Called when the optional "nearby" option is chosen
   */
  onNearbySelected?: (isNearby: boolean) => mixed,

  children: ({|
    value: string,
    onChange: string => mixed,
    onClick: () => mixed,
  |}) => Node,

  /** Will automatically open the geopicker when true */
  autoOpen?: boolean,
|};

/**
 * A geo typeahead. The initial text input is delegated to a render prop, but when the user begins
 * typing this expands to a full-screen search.
 */
const GeoPicker = ({
  placeholder,
  geoName,
  closeOnSelection = false,
  showPopularDestinations = false,
  showNearby = false,
  maxResults = 10,
  onChange,
  onShowHide,
  onNearbySelected = () => {},
  children,
  autoOpen = false,
}: Props) => {
  const { localize } = useLocalize();
  const [textInput, setTextInput] = useState<string>(geoName || '');
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [{ data: popularDestinationsData }] = useQuery({
    query: PopularDestinationsQuery,
    pause: !showPopularDestinations,
  });

  // Popular destinations, for defaults when there are no typeahead results.
  const popularDestinationResults = useMemo(
    () =>
      (popularDestinationsData?.popularDestinations || [])
        .slice(0, maxResults)
        .map(destination => ({
          value: destination?.locationId || -1,
          name: destination?.additionalNames?.longParentAbbreviated || '',
          details: {
            name: destination?.name || '',
            parent_name: destination?.additionalNames?.longOnlyParent || '',
          },
        })),
    [popularDestinationsData, maxResults],
  );

  const fetchers = useMemo(
    () => [
      new TypeaheadJsonFetcher({
        interleaved: true,
        types: ['geo'],
        filter: ['nobroad'],
        name_depth: 1,
        details: true,
        legacy_format: true,
        urlList: false,
        max: maxResults,
      }),
    ],
    [maxResults],
  );

  const open = useCallback(() => {
    setIsOpen(true);
    onShowHide(true);
  }, [onShowHide]);

  const close = useCallback(() => {
    setIsOpen(false);
    onShowHide(false);
  }, [onShowHide]);

  useEffect(
    () => () => {
      onShowHide(false);
    },
    [onShowHide],
  );

  const inputRef = useRef<?HTMLInputElement>(null);
  useLayoutEffect(() => {
    // Focus the typeahead input when it opens. This is a layout effect because on iOS .focus()
    // doesn't work in asynchronous callbacks.
    const input = inputRef.current;
    if (isOpen && input != null) {
      input.focus();
      // Move the cursor to the end of the input
      input.selectionStart = input.value.length;
      input.selectionEnd = input.value.length;
    }
  }, [isOpen]);

  const onSelect = useCallback(
    (value: number, displayName: string, name: string) => {
      onChange(value, displayName);

      if (closeOnSelection) {
        setTextInput(name);
        close();
      }
    },
    [onChange, closeOnSelection, close],
  );

  return (
    <Typeahead fetchers={fetchers}>
      {(results, updateSearch) => {
        if (!isOpen) {
          return children({
            value: textInput,
            onChange: value => {
              open();
              setTextInput(value);
              updateSearch(value);
            },
            onClick: open,
          });
        }

        let resultList = [];
        let showingTypeaheadResults = false;
        let showingPopularDestinations = false;
        if (results.typeaheadjson != null && results.typeaheadjson.length > 0) {
          resultList = results.typeaheadjson;
          showingTypeaheadResults = true;
        } else if (showPopularDestinations) {
          resultList = popularDestinationResults;
          showingPopularDestinations = true;
        }

        return (
          <div className={styles.geoPickerContainer}>
            {autoOpen ? null : (
              <button className={styles.cancelButton} onClick={close}>
                {localize('common_btn_cancel_ffffe209') /* Cancel */}
              </button>
            )}

            <div className={styles.textInputContainer}>
              <input
                className={styles.textInput}
                type="text"
                value={textInput}
                onChange={evt => {
                  setTextInput(evt.currentTarget.value);
                  updateSearch(evt.currentTarget.value);
                }}
                placeholder={placeholder}
                ref={inputRef}
              />
            </div>

            {showNearby && !showingTypeaheadResults && (
              <button className={styles.nearby} onClick={() => onNearbySelected(true)}>
                <Icon className={styles.nearbyIcon} name="nearby-fill" />
                {localize('nearby_use_my_location') /* Use my location */}
              </button>
            )}

            {resultList.length > 0 && showingPopularDestinations && (
              <div className={styles.popularDestinations}>
                {localize('common_Populardestinations') /* Popular destinations */}
              </div>
            )}

            {resultList.length > 0 && (
              <NavigableList>
                {/* eslint-disable-next-line react/display-name */}
                {resultList.map(result => (active, onMouseMove) => (
                  <GeoPickerResult
                    key={result.value}
                    result={result}
                    active={active}
                    onMouseMove={onMouseMove}
                    onSelect={onSelect}
                  />
                ))}
              </NavigableList>
            )}
          </div>
        );
      }}
    </Typeahead>
  );
};

export default memo<Props>(GeoPicker);
